/* eslint-disable no-relative-import-paths/no-relative-import-paths */
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { TFunction } from "i18next";
import { Feature } from "ol";
import { Coordinate } from "ol/coordinate";
import { Geometry, LineString, Point, Polygon } from "ol/geom";
import { fromLonLat, toLonLat } from "ol/proj";
import { Circle, Fill, Icon, Stroke, Style } from "ol/style";
import * as XLSX from "xlsx";

import { Enum_Border_Style } from "src/api/category/enum";
import { Enum_Geometry_Types } from "src/types";
import { Type_Category } from "src/types/category";
import {
    Type_Element,
    Type_Element_Data,
    Type_Formatted_Element,
    Type_Params_Handle_Input_Blur,
    Type_Props_Display_Elements,
    Type_Props_Display_Geometry,
    Type_Props_Get_Element_On_Hover,
} from "src/types/element";

import fa_ban from "../../static/ts/fa-ban";
import fa_brake_warning from "../../static/ts/fa-brake-warning";
import fa_car_burst from "../../static/ts/fa-car-burst";
import fa_helmet_safety from "../../static/ts/fa-helmet-safety";
import fa_person_digging from "../../static/ts/fa-person-digging";
import fa_right_to from "../../static/ts/fa-right-to-bracket";
import fa_road from "../../static/ts/fa-road";
import fa_road_barrier from "../../static/ts/fa-road-barrier";
import fa_parking from "../../static/ts/fa-square-parking";
import fa_exclamation from "../../static/ts/fa-triangle-exclamation";
import fa_user from "../../static/ts/fa-user";

// used to transform coordinates according per type
export const transformCoordinates = (
    geometryType: Enum_Geometry_Types,
    coordinates: Coordinate | Coordinate[] | Coordinate[][],
): Coordinate | Coordinate[] | Coordinate[][] | any => {
    switch (geometryType) {
        case Enum_Geometry_Types.POINT:
            return toLonLat(coordinates as Coordinate);
        case Enum_Geometry_Types.LINESTRING:
            return (coordinates as Coordinate[]).map((elem) => toLonLat(elem));
        case Enum_Geometry_Types.POLYGON:
            return (coordinates as Coordinate[][]).map((ring) =>
                ring.map((ele) => toLonLat(ele)),
            );
        default:
            throw new Error("Unsupported geometry type");
    }
};

// apply the style per type
// Point: use icon with the appropriate color
// Linestring: use stroke with the appropriate color
// Polygon: define the stroke with the fill color
const stylePerGeometry = (category: Type_Category) => {
    const {
        primary_color,
        border_width,
        border_style,
        background_color,
        scale,
        lineDash,
    } = category.style || {};

    const stroke = new Stroke({
        color: primary_color,
        width: border_width !== undefined ? border_width : 1,
        lineDash:
            lineDash ??
            (border_style !== undefined
                ? styleBorder(border_style)
                : styleBorder(Enum_Border_Style.SOLID)),
    });

    switch (category.nature) {
        case Enum_Geometry_Types.POINT:
            return {
                image: new Icon({
                    src: `data:image/svg+xml;base64,${btoa(mapCategoryToSvg[category.picto.split(" ")[1]])}`,
                    scale: scale ?? 0.1,
                    color: primary_color,
                }),
            };

        case Enum_Geometry_Types.LINESTRING:
            return { stroke };

        case Enum_Geometry_Types.POLYGON:
            return {
                stroke: stroke,
                fill: new Fill({
                    color: background_color,
                }),
            };
    }
};

// list of icons prposed for categories
// need to be defined since stylePerGeometry[Point] uses binary data
const mapCategoryToSvg: { [key: string]: string } = {
    "fa-brake-warning": fa_brake_warning,
    "fa-ban": fa_ban,
    "fa-square-parking": fa_parking,
    "fa-right-to-bracket": fa_right_to,
    "fa-car-burst": fa_car_burst,
    "fa-person-digging": fa_person_digging,
    "fa-helmet-safety": fa_helmet_safety,
    "fa-road-barrier": fa_road_barrier,
    "fa-road": fa_road,
    "fa-triangle-exclamation": fa_exclamation,
    "fa-users": fa_user,
};

// used to display eleemnts on the map per type
export const displayElements = ({
    zoom,
    elements,
    categories,
    vectorSource,
}: Type_Props_Display_Elements) => {
    vectorSource.clear();

    elements.forEach((element: Type_Element_Data) => {
        const category: Type_Category | undefined = categories.find(
            (category: Type_Category) => category.id === element.category_id,
        );

        if (!category) return;

        const displayPerGeometry = {
            [Enum_Geometry_Types.POINT]: displayPoint,
            [Enum_Geometry_Types.LINESTRING]: displayLinestring,
            [Enum_Geometry_Types.POLYGON]: displayPolygon,
        };

        const feature = displayPerGeometry[category.nature]({
            zoom,
            element,
            category,
        });
        feature.setProperties({
            id: element.id,
        });
        vectorSource.addFeature(feature);
    });
};

const factorFromZoom = (zoom: number): number => {
    return 1 / (zoom || 1);
};

const displayPoint = ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    zoom,
    element,
    category,
}: Type_Props_Display_Geometry): Feature<Geometry> => {
    const transformedCoords = fromLonLat(
        element.coordinates?.coordinates as Coordinate,
    );
    const feature = new Feature({
        geometry: new Point(transformedCoords),
    });

    const primary_color: string =
        element.style?.primary_color || category.style.primary_color;
    const radius = element.style?.radius || category.style.radius || 3;

    feature.setStyle([
        new Style({
            image: new Circle({
                fill: new Fill({ color: "#FFFFFF" }),
                stroke: new Stroke({ color: primary_color, width: 2 }),
                radius: radius * 4.5,
            }),
        }),
        new Style(
            stylePerGeometry({
                ...category,
                style: {
                    ...category.style,
                    primary_color,
                },
            }),
        ),
    ]);
    return feature;
};

const displayLinestring = ({
    zoom,
    element,
    category,
}: Type_Props_Display_Geometry): Feature<Geometry> => {
    const transformedCoords = (
        element.coordinates?.coordinates as Coordinate[]
    ).map((elem) => fromLonLat(elem));
    const feature = new Feature({
        geometry: new LineString(transformedCoords),
    });

    const border_width =
        element.style?.border_width || category.style.border_width || 0;

    const min_border_width =
        element.style?.min_border_width || category.style.min_border_width || 3;

    const width = Math.max(
        min_border_width,
        border_width * factorFromZoom(zoom),
    );

    const stroke = new Stroke({
        color: "#FFFFFFD0",
        width: width * 3,
    });

    feature.setStyle([
        new Style({
            stroke: stroke,
        }),
        new Style(
            stylePerGeometry({
                ...category,
                style: {
                    ...category.style,
                    primary_color:
                        element.style?.primary_color ||
                        category.style.primary_color,
                    border_style:
                        (element.style?.border_style as Enum_Border_Style) ||
                        (category.style.border_style as Enum_Border_Style),
                    border_width: width,
                },
            }),
        ),
    ]);
    return feature;
};

const displayPolygon = ({
    zoom,
    element,
    category,
}: Type_Props_Display_Geometry): Feature<Geometry> => {
    const transformedCoords = (
        element.coordinates?.coordinates as Coordinate[][]
    ).map((ring) => ring.map((ele) => fromLonLat(ele)));

    const polygonFeature = new Feature({
        geometry: new Polygon(transformedCoords),
    });

    const border_width =
        element.style?.border_width || category.style.border_width || 0;
    const min_border_width =
        element.style?.min_border_width || category.style.min_border_width || 3;

    polygonFeature.setStyle(
        new Style(
            stylePerGeometry({
                ...category,
                style: {
                    primary_color:
                        element.style?.primary_color ||
                        category.style.primary_color,
                    background_color:
                        element.style?.background_color ||
                        category.style.background_color,
                    border_style:
                        (element.style?.border_style as Enum_Border_Style) ||
                        (category.style.border_style as Enum_Border_Style),
                    border_width: Math.max(
                        min_border_width,
                        border_width * factorFromZoom(zoom),
                    ),
                },
            }),
        ),
    );
    return polygonFeature;
};

const styleBorder = (style: string) =>
    style === Enum_Border_Style.DASHED
        ? [10, 10]
        : style === Enum_Border_Style.DOTTED
          ? [2, 6]
          : [];

export const getElementOnHover = ({
    feature,
    elements,
}: Type_Props_Get_Element_On_Hover) => {
    const properties = feature.getProperties();
    return elements.find((item) => item.id === properties.id);
};

export const transformElementsToListStructure = (
    elements: Type_Element_Data[],
    categories: Type_Category[],
    language: string,
) =>
    elements.map((element: Type_Element_Data) => ({
        id: element.id,
        category_id: element.category_id,
        title: element.title,
        description: element.description,
        category: categories.find(
            (category: Type_Category) => category.id === element.category_id,
        )?.title[language],
        start_date: element.start_date,
        end_date: element.end_date,
        parent_id: element.parent_id,
        uuid: element.uuid,
    }));

export const exportToXlsx = (
    formatElements: Type_Formatted_Element[],
    t: TFunction,
) => {
    const worksheetData = formatElements.map((row) => ({
        [t("addElement.event")]: row.title,
        [t("addElement.description")]: row.description,
        [t("addElement.category")]: row.category,
        [t("addElement.startDate")]: dayjs(row.start_date).format("L LT"),
        [t("addElement.endDate")]: dayjs(row.end_date).format("L LT"),
    }));
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

    const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
    });

    const blob = new Blob([excelBuffer]);
    saveAs(blob, `zac_export.xlsx`);
};

// update the context with the correct value
// used by inputs in dynamic forms
export const handleInputBlur = ({
    data,
    selectedValue,
    newElement,
    updateNewElement,
}: Type_Params_Handle_Input_Blur): void => {
    const newInput = { input_id: data.id, value: selectedValue };
    const currentInputs = newElement?.data || [];
    const updatedInputs = currentInputs.some(
        (input: any) => input.input_id === data.id,
    )
        ? currentInputs.map((input: any) =>
              input.input_id === data.id ? newInput : input,
          )
        : [...currentInputs, newInput];

    updateNewElement({
        ...newElement,
        data: updatedInputs,
    } as unknown as Type_Element);
};
